import React, { useState } from 'react';
import { 
  FileText, Download, Star, Shield, DollarSign, TrendingUp, Mail, User, X, 
  Maximize2, Minimize2, ExternalLink, ArrowLeft, ArrowRight, CheckCircle, Copy
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  conversionRate: string;
  fields: number;
  features: string[];
  thumbnail: string;
  previewUrl?: string;
  previewContent?: {
    title: string;
    description: string;
    fields: Array<{
      label: string;
      type: string;
      required?: boolean;
    }>;
  };
}

const templates: Template[] = [
  {
    id: 'tax-return-seminar',
    name: 'Tax Return Qualification Seminar',
    description: 'High-converting seminar registration page with free tax return offer for qualified attendees.',
    category: 'Events',
    conversionRate: '42%',
    fields: 7,
    features: [
      'Qualification criteria',
      'Speaker credentials',
      'Seminar details',
      'Free tax return offer',
      'Trust indicators'
    ],
    thumbnail: 'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg',
    previewUrl: '/l/tax-return-seminar'
  },
  {
    id: 'webinar-registration',
    name: 'Webinar Registration',
    description: 'High-converting webinar registration form with countdown timer and social proof.',
    category: 'Events',
    conversionRate: '32%',
    fields: 5,
    features: [
      'Event details display',
      'Countdown timer',
      'Calendar integration',
      'Automated reminders'
    ],
    thumbnail: 'https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg',
    previewUrl: '/l/webinar-registration'
  },
  {
    id: 'ebook-download',
    name: 'eBook Download',
    description: 'Lead magnet template optimized for financial planning guide downloads.',
    category: 'Content',
    conversionRate: '45%',
    fields: 4,
    features: [
      'Professional eBook preview',
      'Social proof elements',
      'Download tracking',
      'Email integration'
    ],
    thumbnail: 'https://images.pexels.com/photos/7688159/pexels-photo-7688159.jpeg',
    previewUrl: '/l/annuity-guide'
  },
  {
    id: 'tax-preparation',
    name: 'Tax Preparation Offer',
    description: 'Special offer landing page targeting seniors for tax preparation services.',
    category: 'Services',
    conversionRate: '38%',
    fields: 6,
    features: [
      'Age verification',
      'Special offer highlight',
      'Trust indicators',
      'Contact preference selection'
    ],
    thumbnail: 'https://images.pexels.com/photos/6863250/pexels-photo-6863250.jpeg',
    previewUrl: '/l/tax-preparation'
  },
  {
    id: 'consultation-booking',
    name: 'Consultation Booking',
    description: 'Multi-step consultation booking form with qualification questions.',
    category: 'Services',
    conversionRate: '28%',
    fields: 6,
    features: [
      'Availability checker',
      'Client qualification',
      'Meeting preferences',
      'Auto-confirmation'
    ],
    thumbnail: 'https://images.pexels.com/photos/8867482/pexels-photo-8867482.jpeg',
    previewUrl: '/l/consultation-booking'
  },
  {
    id: 'newsletter-signup',
    name: 'Newsletter Signup',
    description: 'Simple but effective newsletter signup with interest targeting.',
    category: 'Email',
    conversionRate: '52%',
    fields: 3,
    features: [
      'Interest selection',
      'Preference center',
      'Welcome sequence'
    ],
    thumbnail: 'https://images.pexels.com/photos/8867431/pexels-photo-8867431.jpeg',
    previewUrl: '/l/newsletter-signup'
  },
  {
    id: 'retirement-assessment',
    name: 'Retirement Assessment',
    description: 'Interactive retirement readiness assessment form.',
    category: 'Assessment',
    conversionRate: '38%',
    fields: 8,
    features: [
      'Risk assessment',
      'Goal planning',
      'Income calculator',
      'Results summary'
    ],
    thumbnail: 'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg',
    previewUrl: '/l/retirement-calculator'
  },
  {
    id: 'funeral-trust',
    name: 'Funeral Trust Planning',
    description: 'Sensitive, professional lead capture for funeral trust planning services.',
    category: 'Estate Planning',
    conversionRate: '41%',
    fields: 7,
    features: [
      'Empathetic messaging',
      'Asset protection benefits',
      'Tax advantage explanation',
      'Free guide offer'
    ],
    thumbnail: 'https://images.pexels.com/photos/2559941/pexels-photo-2559941.jpeg',
    previewUrl: '/l/funeral-trust'
  },
  {
    id: 'radio-show',
    name: 'Radio Show Listener Offer',
    description: 'Targeted landing page for radio show listeners with exclusive offer.',
    category: 'Media',
    conversionRate: '47%',
    fields: 8,
    features: [
      'Radio show branding',
      'Host endorsement',
      'Exclusive offer details',
      'Qualification questions'
    ],
    thumbnail: 'https://images.pexels.com/photos/3783471/pexels-photo-3783471.jpeg',
    previewUrl: '/l/radio-show'
  }
];

const TemplateGallery: React.FC = () => {
  const [lastClickedItem, setLastClickedItem] = useState<string | null>(null);

  const handleTemplateClick = (template: Template) => {
    // Open the template in a new window/tab
    if (template.previewUrl) {
      window.open(template.previewUrl, '_blank');
      
      // Highlight the clicked item briefly for visual feedback
      setLastClickedItem(template.id);
      setTimeout(() => {
        setLastClickedItem(null);
      }, 2000);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Landing Page Templates</h1>

      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {templates.map((template) => (
          <div 
            key={template.id} 
            className={`bg-white rounded-lg shadow-sm border hover:shadow-lg transition-all duration-300 ${
              lastClickedItem === template.id 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 hover:border-primary'
            } cursor-pointer`}
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={template.thumbnail}
                alt={template.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4 bg-white/90 px-2 py-1 rounded-full text-sm font-medium text-gray-700">
                {template.conversionRate} conversion
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {template.name}
              </h3>
              <p className="text-gray-600 mb-4">
                {template.description}
              </p>
              
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">{template.fields} fields</span>
              </div>
              
              <div className="space-y-2">
                {template.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex gap-3">
                <a
                  href={template.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLastClickedItem(template.id);
                    setTimeout(() => {
                      setLastClickedItem(null);
                    }, 2000);
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  Preview
                </a>
                <a
                  href={template.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLastClickedItem(template.id);
                    setTimeout(() => {
                      setLastClickedItem(null);
                    }, 2000);
                  }}
                >
                  <Copy className="w-4 h-4" />
                  Use Template
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplateGallery;